import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { invalidatePaths, useApiMutation, useApiQuery } from "@/lib/query";
import type { ItemPage } from "@/features/experiments/types";
import type {
  CodeCheck,
  Evaluator,
  EvaluatorCreate,
  EvaluatorPatch,
  EvaluatorTemplate,
  PathInfo,
  PreviewRequest,
  PreviewResult,
  SampleDocument,
} from "./types";

export interface EvaluatorFilter {
  dataset_id?: string | undefined;
  agent_id?: string | undefined;
}

export interface SampleRef {
  dataset_item_id?: string | undefined;
  item_result_id?: string | undefined;
  /** An observation in a trace source: connection and trace together, plus the
   *  observation's id unless it is the trace's root. */
  connection_id?: string | undefined;
  trace_id?: string | undefined;
  observation_id?: string | undefined;
}

export const hasSample = (ref: SampleRef) =>
  !!ref.dataset_item_id || !!ref.item_result_id || (!!ref.connection_id && !!ref.trace_id);

export const useEvaluators = (filter: EvaluatorFilter = {}) =>
  useApiQuery<Evaluator[]>("/evaluators", { ...filter });

/** Paths a variable can map to, for this dataset (and agent). */
export const useEvaluatorPaths = (filter: EvaluatorFilter, enabled = true) =>
  useApiQuery<PathInfo[]>("/evaluators/paths", { ...filter }, { enabled });

/** The whole document a mapping reads into, for one item or past result; for an
 *  observation, the observation as Langfuse returns it. */
export const useEvaluatorSample = (ref: SampleRef) =>
  useApiQuery<SampleDocument>("/evaluators/sample", { ...ref }, { enabled: hasSample(ref) });

/** Every version of this evaluator's key, newest first. */
export const evaluatorVersionsPath = (id: string) => `/evaluators/${id}/versions`;
export const useEvaluatorVersions = (id: string, enabled = true) =>
  useApiQuery<Evaluator[]>(evaluatorVersionsPath(id), {}, { enabled });

/** Ready-made definitions; picking one only pre-fills the configure screen. */
export const useEvaluatorTemplates = (enabled = true) =>
  useApiQuery<EvaluatorTemplate[]>("/evaluators/templates", {}, { enabled, staleTime: Infinity });

/** The fixed rules a code check can apply. */
export const useCodeChecks = (enabled = true) =>
  useApiQuery<CodeCheck[]>("/evaluators/checks", {}, { enabled, staleTime: Infinity });

/** The first page of a past experiment's results, to test an evaluator on one. */
export const useResultItems = (experimentId: string) =>
  useApiQuery<ItemPage>(
    `/experiments/${experimentId}/items`,
    { page: 1, page_size: 100 },
    { enabled: !!experimentId },
  );

export const useCreateEvaluator = () =>
  useApiMutation<Evaluator, EvaluatorCreate>("post", "/evaluators", { invalidate: ["/evaluators"] });

/** Any edit saves a new version; lists and version lists refetch. */
export const usePatchEvaluator = (id: string) =>
  useApiMutation<Evaluator, EvaluatorPatch>("patch", `/evaluators/${id}`, { invalidate: ["/evaluators"] });

/** PATCH several evaluators at once (the new-experiment "also save as default"). */
export function usePatchEvaluators() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (patches: Array<{ id: string; body: EvaluatorPatch }>) =>
      Promise.all(patches.map((p) => api.patch<Evaluator>(`/evaluators/${p.id}`, p.body))),
    onSuccess: () => invalidatePaths(queryClient, ["/evaluators"]),
  });
}

/** Score one sample with an unsaved definition. Nothing is stored. */
export const usePreviewEvaluator = () =>
  useApiMutation<PreviewResult, PreviewRequest>("post", "/evaluators/preview");
